import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  UserX,
  Users,
  Filter,
  Search,
  ChevronDown,
  Check,
} from "lucide-react";
import { studentsAPI, trainersAPI, notificationsAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import { useTheme } from "../contexts/ThemeContext";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../App";
import StudentModal from "../components/ui/StudentModal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import { useCRUD } from "../hooks/useCRUD";
import { useTableData } from "../hooks/useTableData";
import { useFilters } from "../hooks/useFilters";
import { useWindowWidth } from "../hooks/useWindowWidth";
import PageHeader from "../components/common/PageHeader";
import { usePermissions } from "../components/HasPermission";
import StatsCards from "../components/common/StatsCards";
import DataTable from "../components/common/DataTable/DataTable";
import { getInitials, getAvatarColor } from "../utils/avatarHelpers";
import { STATUS_CONFIGS } from "../utils/constants";
import FilterSidebar from "../components/common/FilterSidebar";

const StudentsPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { selectedOrganizationId } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const windowWidth = useWindowWidth();
  const { hasPermission } = usePermissions();

  // Modular hooks for CRUD operations
  const {
    data: students,
    setData: setStudents,
    loading,
    actionLoading,
    error,
    setError,
    fetchAll: fetchStudents,
    create: createStudent,
    update: updateStudent,
    remove: removeStudent,
  } = useCRUD(studentsAPI, selectedOrganizationId);

  // Additional state for trainers
  const [trainers, setTrainers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    student: null,
  });
  const [assignTrainerConfirm, setAssignTrainerConfirm] = useState({
    isOpen: false,
    student: null,
    trainerId: null,
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const [openStatusDropdownId, setOpenStatusDropdownId] = useState(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleGlobalClick = () => setOpenStatusDropdownId(null);
    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, []);

  // Filter configuration
  const filterConfigs = [
    {
      key: "status",
      type: "exact",
      accessor: (item) => item.studentStatus,
      defaultValue: "active",
    },
    {
      key: "trainer",
      type: "custom",
      accessor: (item) => item.mainTrainerId,
      filterFn: (item, filterValue) => {
        if (filterValue === "no-trainer") return !item.mainTrainerId;
        return item.mainTrainerId === filterValue;
      },
      defaultValue: "all",
    },
    {
      key: "gender",
      type: "exact",
      accessor: (item) => item.gender,
      defaultValue: "all",
    },
  ];

  // Use filters hook
  const {
    filters,
    filteredData: filterByFilters,
    setFilter,
    resetFilters,
  } = useFilters(students, filterConfigs);

  // Use table data hook with search
  const {
    paginatedData: paginatedStudents,
    totalItems: filteredCount,
    currentPage,
    rowsPerPage,
    totalPages,
    startIndex,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = useTableData(filterByFilters, ["name", "surname", "email", "phone"]);

  // Fetch trainers (memoized to prevent duplicate calls)
  const fetchTrainers = useCallback(async () => {
    if (!selectedOrganizationId) return;
    try {
      const response = await trainersAPI.getAll(selectedOrganizationId);
      const trainersList = response.data.data || [];
      // Deduplicate trainers by ID to prevent React key warnings
      const uniqueTrainers = trainersList.filter(
        (trainer, index, self) =>
          index === self.findIndex((t) => t.id === trainer.id),
      );
      setTrainers(uniqueTrainers);
    } catch (err) {
      console.error("Error fetching trainers:", err);
      // Silent error or toast? usually silent for aux data
    }
  }, [selectedOrganizationId]);

  // Fetch data on mount and when organization changes
  useEffect(() => {
    if (selectedOrganizationId) {
      fetchStudents();
      fetchTrainers();
    }
  }, [selectedOrganizationId, fetchStudents, fetchTrainers]);

  // CRUD handlers
  const handleCreate = async (studentData) => {
    // CRITICAL FIX: Pass studentData.organizationId as the third argument (overrideOrgId)
    // This ensures creation works even if global context is "all" or mismatched.
    const result = await createStudent(
      studentData,
      null, // hooks/useCRUD handles toasts internally or we do it here. let's assume we do it here for specificity.
      studentData.organizationId, // Override Organization ID
    );

    if (result) {
      toast.success(
        `${studentData.name} ${studentData.surname} has been added to the system.`,
      );

      // Frontend Notification
      try {
        await notificationsAPI.create(
          result.organizationId || selectedOrganizationId,
          {
            title: "Yeni Öğrenci Kaydı",
            body: `${studentData.name} ${studentData.surname
              } sisteme yeni öğrenci olarak eklendi. (Oluşturan: ${user?.name || user?.email || "Bilinmiyor"
              })`,
            type: "new_student",
            receivers: [],
          },
        );
      } catch (notifErr) {
        console.error("Notification failed:", notifErr);
      }
      setIsModalOpen(false);
    }
  };

  const handleUpdate = async (studentData) => {
    const result = await updateStudent(
      editingStudent.id,
      studentData,
      null,
      studentData.organizationId,
    );
    if (result) {
      toast.success(
        `${studentData.name} ${studentData.surname} has been updated successfully.`,
      );
      setIsModalOpen(false);
      setEditingStudent(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.student) return;

    try {
      const result = await removeStudent(
        deleteConfirm.student.id,
        null, // No override needed for delete usually, or maybe organizationId if API requires
        deleteConfirm.student.organizationId, // Defensive: pass orgId if needed
      );

      if (result) {
        toast.success(
          `${deleteConfirm.student.name} ${deleteConfirm.student.surname} has been deleted.`,
        );
        setDeleteConfirm({ isOpen: false, student: null });
      }
    } catch (err) {
      // Error handled by useCRUD/toast
      setDeleteConfirm({ isOpen: false, student: null });
    }
  };

  const handleAssignTrainer = async () => {
    try {
      const response = await studentsAPI.assignTrainer(
        selectedOrganizationId,
        assignTrainerConfirm.student.id,
        assignTrainerConfirm.trainerId,
      );
      setStudents((prev) =>
        prev.map((student) =>
          student.id === assignTrainerConfirm.student.id
            ? response.data.data
            : student,
        ),
      );
      setAssignTrainerConfirm({
        isOpen: false,
        student: null,
        trainerId: null,
      });
      toast.success(
        `Trainer has been assigned to ${assignTrainerConfirm.student.name} ${assignTrainerConfirm.student.surname}.`,
      );
    } catch (err) {
      console.error("Error assigning trainer:", err);
      toast.error("Failed to assign trainer");
    }
  };

  const handleRemoveTrainer = async (student) => {
    try {
      const response = await studentsAPI.removeTrainer(
        selectedOrganizationId,
        student.id,
      );
      setStudents((prev) =>
        prev.map((s) => (s.id === student.id ? response.data.data : s)),
      );
      toast.success(
        `Trainer has been removed from ${student.name} ${student.surname}.`,
      );
    } catch (err) {
      console.error("Error removing trainer:", err);
      toast.error("Failed to remove trainer");
    }
  };

  const handleStatusChange = async (student, newStatus) => {
    try {
      const result = await updateStudent(
        student.id,
        { ...student, studentStatus: newStatus },
        null,
        student.organizationId
      );
      if (result) {
        toast.success(
          t("studentsPage.statusUpdated", {
            name: `${student.name} ${student.surname}`,
            defaultValue: `${student.name} ${student.surname} durumu güncellendi.`,
          }),
        );
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Modal handlers
  const openEditModal = (student) => {
    setEditingStudent(student);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingStudent(null);
    setIsModalOpen(true);
  };

  // Helper functions
  const getTrainerName = (trainerId) => {
    const trainer = trainers.find((t) => t.id === trainerId);
    return trainer ? trainer.name : "-";
  };

  const getStatusBadge = (student) => {
    const status = student.studentStatus;
    const config = STATUS_CONFIGS[status] || STATUS_CONFIGS.active;
    const isOpen = openStatusDropdownId === student.id;

    if (!hasPermission("update:students")) {
      return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} shadow-sm`}
        >
          {t(`studentsPage.filters.${status}`, config.label)}
        </span>
      );
    }

    return (
      <div className={`relative inline-block ${isOpen ? "z-[999]" : ""}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenStatusDropdownId(isOpen ? null : student.id);
          }}
          className={`
            flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold 
            ${config.bg} ${config.text} shadow-md transition-all duration-200 
            hover:scale-105 active:scale-95 group
          `}
        >
          {t(`studentsPage.filters.${status}`, config.label)}
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div
            className={`
              absolute left-0 mt-2 w-36 z-[100] p-1.5 overflow-hidden
              ${colors.cardBg} ${colors.cardBorder} border rounded-2xl shadow-2xl
              animate-in fade-in zoom-in duration-200 origin-top-left
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`text-[9px] font-bold ${colors.textMuted} px-2 mb-1.5 uppercase tracking-wider`}>
              {t("studentsPage.table.actions")}
            </div>
            {Object.entries(STATUS_CONFIGS).map(([key, cfg]) => (
              <button
                key={key}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange(student, key);
                  setOpenStatusDropdownId(null);
                }}
                className={`
                  w-full flex items-center px-2.5 py-2 rounded-xl mb-1 last:mb-0
                  transition-all duration-150 group/item
                  ${key === status ? `${colors.bgHoverStrong}` : `hover:${colors.bgHover}`}
                `}
              >
                <div className={`w-2.5 h-2.5 rounded-full mr-2.5 ${cfg.bg} shadow-sm group-hover/item:scale-110 transition-transform`} />
                <span className={`text-[11px] font-semibold ${key === status ? colors.textPrimary : colors.textSecondary}`}>
                  {t(`studentsPage.filters.${key}`, cfg.label)}
                </span>
                {key === status && <Check className="w-3.5 h-3.5 ml-auto text-blue-500" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const navigateToStudentDetails = (student) => {
    navigate(`/students/${student.id}`, {
      state: {
        student,
        organizationId: selectedOrganizationId,
      },
    });
  };

  // Stats calculation
  const activeStudents = students.filter((s) => s.studentStatus === "active");
  const studentsWithTrainer = students.filter((s) => s.mainTrainerId);
  const studentsWithoutTrainer = students.filter((s) => !s.mainTrainerId);

  const stats = [
    {
      label: t("studentsPage.stats.total"),
      value: students.length,
      gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    },
    {
      label: t("studentsPage.stats.active"),
      value: activeStudents.length,
      gradient: "bg-gradient-to-r from-green-500 to-green-600",
    },
    {
      label: t("studentsPage.stats.withTrainer"),
      value: studentsWithTrainer.length,
      gradient: "bg-gradient-to-r from-purple-500 to-purple-600",
    },
    {
      label: t("studentsPage.stats.noTrainer"),
      value: studentsWithoutTrainer.length,
      gradient: "bg-gradient-to-r from-orange-500 to-orange-600",
    },
  ];

  // Filter options
  const filterOptions = [
    {
      key: "search",
      type: "search",
      label: t("studentsPage.searchPlaceholder"),
      placeholder: t("studentsPage.searchPlaceholder"),
      value: searchTerm,
      onChange: handleSearch,
    },
    {
      key: "status",
      type: "select",
      label: t("studentsPage.filters.status"),
      value: filters.status,
      onChange: (value) => setFilter("status", value),
      options: [
        { value: "all", label: t("studentsPage.filters.allStatuses") },
        { value: "active", label: t("studentsPage.filters.active") },
        { value: "inactive", label: t("studentsPage.filters.inactive") },
        { value: "suspended", label: t("studentsPage.filters.suspended") },
        { value: "graduated", label: t("studentsPage.filters.graduated") },
      ],
    },
    {
      key: "trainer",
      type: "select",
      label: t("studentsPage.filters.trainer"),
      value: filters.trainer,
      onChange: (value) => setFilter("trainer", value),
      options: [
        { value: "all", label: t("studentsPage.filters.allTrainers") },
        { value: "no-trainer", label: t("studentsPage.filters.noTrainer") },
        ...trainers.map((trainer) => ({
          value: trainer.id,
          label: trainer.name,
        })),
      ],
    },
  ];

  // Table columns configuration - Progressive responsive hiding
  // >= 1280px: All columns | >= 1100px: hide Contact | >= 900px: hide Status | < 900px: only Name + Actions
  const columns = [
    {
      key: "student",
      label: t("studentsPage.table.student"),
      width: "3fr",
      render: (student) => (
        <div className="flex items-center space-x-3.5">
          {student.profilePicture ? (
            <img
              src={student.profilePicture}
              alt={student.name}
              className="w-10 h-10 rounded-full object-cover shadow-sm flex-shrink-0"
            />
          ) : (
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0 ${getAvatarColor(
                student.name,
              )}`}
            >
              {getInitials(student.name, student.surname)}
            </div>
          )}
          <div className="min-w-0">
            <div className={`font-semibold ${colors.textPrimary} truncate`}>
              {student.name} {student.surname}
            </div>
            <div className={`text-xs ${colors.textTertiary} truncate mt-0.5`}>
              {student.email || student.phone}
            </div>
          </div>
        </div>
      ),
    },
    // Contact: visible >= 1280px
    ...(windowWidth >= 1280
      ? [
        {
          key: "contact",
          label: t("studentsPage.table.contact"),
          width: "1.2fr",
          render: (student) => (
            <div>
              <div className={`text-sm ${colors.textSecondary}`}>
                {student.phone}
              </div>
              {student.age && (
                <div className={`text-xs ${colors.textMuted}`}>
                  {t("studentsPage.table.age")}: {student.age}
                </div>
              )}
            </div>
          ),
        },
      ]
      : []),
    // Status: visible >= 1100px
    ...(windowWidth >= 1100
      ? [
        {
          key: "status",
          label: t("studentsPage.table.status"),
          width: "0.8fr",
          render: (student) => getStatusBadge(student),
        },
      ]
      : []),
    // Trainer: visible >= 900px
    ...(windowWidth >= 900
      ? [
        {
          key: "trainer",
          label: t("studentsPage.table.mainTrainer"),
          width: "1.5fr",
          render: (student) => (
            <div className="flex items-center space-x-2 w-full">
              {student.mainTrainerId ? (
                <div className="flex items-center justify-between w-full group">
                  <span
                    className={`text-sm ${colors.textSecondary} truncate mr-2`}
                  >
                    {getTrainerName(student.mainTrainerId)}
                  </span>
                  {hasPermission("assign:trainers") && (
                    <button
                      onClick={() => handleRemoveTrainer(student)}
                      disabled={actionLoading}
                      className={`p-1.5 text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-lg transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50`}
                      title={t("studentsPage.removeTrainer")}
                    >
                      <UserX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ) : hasPermission("assign:trainers") ? (
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      setAssignTrainerConfirm({
                        isOpen: true,
                        student,
                        trainerId: e.target.value,
                      });
                    }
                  }}
                  disabled={actionLoading}
                  className={`w-full text-xs ${colors.borderSecondary} rounded-lg px-2 py-1.5 ${colors.paginationBg} ${colors.inputText} shadow-sm focus:outline-none focus:ring-2 ${colors.inputFocus} disabled:opacity-50 appearance-none cursor-pointer bg-no-repeat bg-right pr-6`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundSize: "1.25rem",
                  }}
                  defaultValue=""
                >
                  <option value="">{t("studentsPage.assign")}</option>
                  {trainers.map((trainer) => (
                    <option key={trainer.id} value={trainer.id}>
                      {trainer.name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className={`text-sm ${colors.textSecondary}`}>-</span>
              )}
            </div>
          ),
        },
      ]
      : []),
    {
      key: "actions",
      label: t("studentsPage.table.actions"),
      width: "1fr",
      render: (student) => (
        <div className="flex items-center justify-end space-x-2 w-full flex-shrink-0">
          <button
            onClick={() => navigateToStudentDetails(student)}
            className="btn-action btn-action-primary"
            title={t("common.viewDetails")}
          >
            <Eye className="w-4 h-4" />
          </button>

          {hasPermission("update:students") && (
            <button
              onClick={() => openEditModal(student)}
              disabled={actionLoading}
              className="btn-action btn-action-warning"
              title={t("common.edit")}
            >
              <Edit className="w-4 h-4" />
            </button>
          )}

          {hasPermission("delete:students") && (
            <button
              onClick={() => setDeleteConfirm({ isOpen: true, student })}
              disabled={actionLoading}
              className="btn-action btn-action-danger"
              title={t("common.delete")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Mobile card renderer - Compact & Responsive
  const renderMobileCard = (student) => (
    <div
      className={`p-3 ${colors.tableRowHover} rounded-xl shadow-sm border ${colors.border} mb-3`}
    >
      {/* Top Row: Avatar, Info, Status */}
      <div className="flex items-start space-x-3 mb-3">
        {student.profilePicture ? (
          <img
            src={student.profilePicture}
            alt={student.name}
            className="w-10 h-10 rounded-full object-cover shadow-sm flex-shrink-0"
          />
        ) : (
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0 ${getAvatarColor(
              student.name,
            )}`}
          >
            {getInitials(student.name, student.surname)}
          </div>
        )}

        {/* Helper for responsive truncated text */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div
              className={`font-semibold ${colors.textPrimary} text-sm truncate pr-2`}
            >
              {student.name} {student.surname}
            </div>
            {/* Status Badge */}
            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {getStatusBadge(student)}
            </div>
          </div>
          <div className={`text-xs ${colors.textTertiary} truncate mt-0.5`}>
            {student.email}
          </div>
          <div className={`text-xs ${colors.textTertiary} mt-0.5`}>
            {student.phone}
          </div>
        </div>
      </div>

      {/* Middle Row: Details & Trainer */}
      <div className="mb-3 pl-1 sm:pl-[52px] text-xs space-y-1">
        {" "}
        {/* Align with text start */}
        {student.age && (
          <div className="flex items-center">
            <span className={`${colors.textMuted} mr-1`}>
              {t("studentsPage.table.age")}:
            </span>
            <span className={colors.textSecondary}>{student.age}</span>
          </div>
        )}
        <div className="flex items-center flex-wrap">
          <span className={`${colors.textMuted} mr-1`}>
            {t("studentsPage.table.mainTrainer")}:
          </span>
          {student.mainTrainerId ? (
            <div className="flex items-center">
              <span className={`${colors.textSecondary} font-medium mr-2`}>
                {getTrainerName(student.mainTrainerId)}
              </span>
              {hasPermission("assign:trainers") && (
                <button
                  onClick={() => handleRemoveTrainer(student)}
                  disabled={actionLoading}
                  className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                  title={t("studentsPage.removeTrainer")}
                >
                  <UserX className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : hasPermission("assign:trainers") ? (
            <select
              onChange={(e) => {
                if (e.target.value) {
                  setAssignTrainerConfirm({
                    isOpen: true,
                    student,
                    trainerId: e.target.value,
                  });
                }
              }}
              disabled={actionLoading}
              className={`text-xs ${colors.borderSecondary} rounded px-1.5 py-0.5 ${colors.paginationBg} ${colors.inputText} focus:outline-none focus:ring-1 ${colors.inputFocus}`}
              defaultValue=""
            >
              <option value="">{t("studentsPage.assign")}</option>
              {trainers.map((trainer) => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </option>
              ))}
            </select>
          ) : (
            <span className={`text-sm ${colors.textSecondary}`}>-</span>
          )}
        </div>
      </div>

      {/* Bottom Row: Actions */}
      <div
        className={`flex flex-wrap items-center justify-between pt-2 border-t ${colors.border} gap-2`}
      >
        <button
          onClick={() => navigateToStudentDetails(student)}
          className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40 transition-colors font-medium text-xs"
          title={t("common.viewDetails")}
        >
          <Eye className="w-4 h-4 mr-1.5" />
          {t("common.view")}
        </button>

        {hasPermission("update:students") && (
          <button
            onClick={() => openEditModal(student)}
            disabled={actionLoading}
            className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/40 transition-colors disabled:opacity-50 font-medium text-xs"
            title={t("common.edit")}
          >
            <Edit className="w-4 h-4 mr-1.5" />
            {t("common.edit")}
          </button>
        )}

        {hasPermission("delete:students") && (
          <button
            onClick={() => setDeleteConfirm({ isOpen: true, student })}
            disabled={actionLoading}
            className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50 font-medium text-xs"
            title={t("common.delete")}
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {t("common.delete")}
          </button>
        )}
      </div>
    </div>
  );

  // Empty state
  if (!selectedOrganizationId) {
    return (
      <div className={`flex h-full ${colors.bgPrimary} p-6`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12 max-w-md">
            <div
              className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}
            >
              <Users className={`w-8 h-8 ${colors.emptyStateIcon}`} />
            </div>
            <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2`}>
              {t("studentsPage.selectOrg.title")}
            </h3>
            <p className={colors.textTertiary}>
              {t("studentsPage.selectOrg.message")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-full px-3 pb-3 sm:px-4 sm:pb-4 gap-4 overflow-hidden`}>
      {/* Scoped CSS for open dropdown row stacking (page-local scroll area only) */}
      {openStatusDropdownId && (
        <style>
          {`
            [data-row-id="${openStatusDropdownId}"] {
              z-index: 100 !important;
              position: relative !important;
            }
            /* Prevent clipping at the bottom of the table */
            .students-scroll {
              padding-bottom: 200px !important;
            }
          `}
        </style>
      )}
      {/* Filters Sidebar */}
      <FilterSidebar
        isOpen={isFiltersOpen}
        onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
        filters={filterOptions}
        onReset={resetFilters}
      />

      {/* Main Content */}
      <div
        className={`
        flex-1 flex flex-col overflow-hidden
        ${colors.cardBg} backdrop-blur-md bg-opacity-80
        ${colors.cardBorder} border rounded-[2rem] shadow-xl
        relative transition-all duration-500
      `}
      >
        {/* Toggle Filter Button (desktop, collapsed state) */}
        {!isFiltersOpen && (
          <div className="hidden md:block absolute top-6 left-6 z-10">
            <button
              onClick={() => setIsFiltersOpen(true)}
              className={`p-3 ${colors.bgHoverStrong} rounded-2xl shadow-sm transition-all hover:scale-110`}
            >
              <Filter className={`w-5 h-5 ${colors.textSecondary}`} />
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 md:p-8 custom-scrollbar students-scroll">
          {/* Header Area */}
          <div
            className={`flex flex-col md:flex-row justify-between mb-6 ${!isFiltersOpen ? "md:ml-12" : ""
              }`}
          >
            {/* Mobile Search Bar */}
            <div className="md:hidden mb-4 w-full">
              <div className="relative">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.textMuted}`}
                />
                <input
                  type="text"
                  placeholder={t("studentsPage.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`
                    w-full pl-10 pr-4 py-3
                    ${colors.inputBg} ${colors.inputBorder} border
                    rounded-xl text-sm font-medium ${colors.inputText} 
                    focus:outline-none focus:ring-2 focus:ring-blue-500/50
                    shadow-sm
                  `}
                />
              </div>
            </div>

            <PageHeader
              title={t("studentsPage.title")}
              count={filteredCount}
              onRefresh={fetchStudents}
              onAdd={hasPermission("create:students") ? openCreateModal : null}
              addButtonText={t("studentsPage.addStudent")}
              addButtonTextMobile={t("studentsPage.addStudentMobile")}
              loading={loading}
              actionLoading={actionLoading}
              noPadding={true} // Add this prop to PageHeader maybe, or just styling wrapper
            />
          </div>

          {/* Stats */}
          <div className="mb-8">
            <StatsCards stats={stats} />
          </div>

          {/* Data Table */}
          <DataTable
            data={paginatedStudents}
            columns={columns}
            loading={loading}
            actionLoading={actionLoading}
            error={error}
            onClearError={() => setError(null)}
            renderMobileCard={renderMobileCard}
            emptyState={{
              icon: Users,
              title: t("studentsPage.emptyState.title"),
              message:
                searchTerm ||
                  filters.status !== "all" ||
                  filters.trainer !== "all" ||
                  filters.gender !== "all"
                  ? t("studentsPage.emptyState.messageSearch")
                  : t("studentsPage.emptyState.messageDefault"),
              action:
                !searchTerm &&
                  filters.status === "all" &&
                  filters.trainer === "all" &&
                  filters.gender === "all"
                  ? {
                    label: t("studentsPage.addStudent"),
                    onClick: openCreateModal,
                    icon: Plus,
                  }
                  : null,
            }}
            pagination={{
              currentPage,
              totalPages,
              rowsPerPage,
              totalItems: filteredCount,
              startIndex,
              onPageChange: handlePageChange,
              onRowsPerPageChange: handleRowsPerPageChange,
            }}
          />
        </div>
      </div>

      {/* Student Modal */}
      <StudentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingStudent(null);
        }}
        onSubmit={editingStudent ? handleUpdate : handleCreate}
        student={editingStudent}
        trainers={trainers}
        title={
          editingStudent
            ? t("studentsPage.editStudentTitle")
            : t("studentsPage.addStudentTitle")
        }
        loading={actionLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, student: null })}
        onConfirm={handleDelete}
        title={t("studentsPage.deleteStudentTitle")}
        message={t("studentsPage.deleteStudentMessage", {
          name: `${deleteConfirm.student?.name} ${deleteConfirm.student?.surname}`,
        })}
        confirmText={t("modals.confirmDelete")}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />

      {/* Assign Trainer Confirmation Dialog */}
      <ConfirmDialog
        isOpen={assignTrainerConfirm.isOpen}
        onClose={() =>
          setAssignTrainerConfirm({
            isOpen: false,
            student: null,
            trainerId: null,
          })
        }
        onConfirm={handleAssignTrainer}
        title={t("studentsPage.assignTrainerTitle")}
        message={t("studentsPage.assignTrainerMessage", {
          trainerName: getTrainerName(assignTrainerConfirm.trainerId),
          studentName: `${assignTrainerConfirm.student?.name} ${assignTrainerConfirm.student?.surname}`,
        })}
        confirmText={t("modals.confirmAssign")}
        confirmButtonClass="bg-green-600 hover:bg-green-700 text-white"
      />
    </div>
  );
};

export default StudentsPage;
